import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n/I18nContext";
import { useSiteContent } from "../context/SiteContentContext";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { ArrowRight, Heart } from "lucide-react";
import InstagramGallery from "../components/InstagramGallery";

const HERO_ILLUSTRATION = "https://customer-assets-7cd3h4nn.emergentagent.net/job_ceramic-shop-18/artifacts/hljfdu6p_Kortti-Katja.jpeg";
const SHOWCASE_STACK = "https://customer-assets-7cd3h4nn.emergentagent.net/job_ceramic-shop-18/artifacts/hntemrqm_Pikkurumiluksia2.webp";
const SHOWCASE_BUNNY = "https://customer-assets-7cd3h4nn.emergentagent.net/job_ceramic-shop-18/artifacts/bmiubr78_Pikkrumiluksia3.webp";
const SHOWCASE_TRIO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_ceramic-shop-18/artifacts/cdcx3yai_Pikkurumiluksia4.webp";

export default function Home() {
  const { t, lang } = useI18n();
  const { content } = useSiteContent();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const featured = products.filter((p) => p.featured).slice(0, 4);
  const c = (key) => content[key]?.[lang] || content[key]?.en || "";

  return (
    <main data-testid="home-page">
      {/* Hero — dusty teal like the brand illustration */}
      <section
        className="relative overflow-hidden"
        style={{ background: "hsl(var(--teal-bg))" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 fade-in">
              <div className="label-caps text-white/80 mb-6" data-testid="hero-eyebrow">
                {c("hero_eyebrow")}
              </div>
              <h1
                className="font-hand text-6xl md:text-8xl lg:text-9xl leading-[0.95] whitespace-pre-line mb-10 text-white"
                data-testid="hero-title"
              >
                {c("hero_title")}
              </h1>
              <p className="text-base md:text-lg text-white/90 max-w-xl leading-relaxed mb-10">
                {c("hero_lede")}
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <Link to="/shop" className="btn-primary" data-testid="hero-cta-shop">
                  {t("hero.cta_shop")}
                  <ArrowRight strokeWidth={2} className="w-4 h-4" />
                </Link>
                <Link
                  to="/story"
                  className="btn-outline"
                  style={{ borderColor: "white", color: "white" }}
                  data-testid="hero-cta-story"
                >
                  {t("hero.cta_story")}
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 fade-in flex justify-center" style={{ animationDelay: "150ms" }}>
              <img
                src={HERO_ILLUSTRATION}
                alt="Pikkurumiluksia"
                className="w-full max-w-[420px] float-slow"
                data-testid="hero-illustration"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Showcase — real studio pieces */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24" data-testid="showcase-section">
        <div className="mb-14 max-w-2xl">
          <div className="label-caps text-muted-foreground mb-3 flex items-center gap-2">
            <Heart strokeWidth={2} className="w-4 h-4 text-[hsl(var(--primary))] fill-[hsl(var(--primary))]" />
            00 — {t("showcase.eyebrow")}
          </div>
          <h2 className="font-hand text-5xl md:text-7xl leading-[1] whitespace-pre-line mb-5">
            {c("showcase_title")}
          </h2>
          <p className="text-base md:text-lg text-muted-foreground">{c("showcase_body")}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          <div className="md:col-span-7 md:row-span-2">
            <div className="product-image-wrap aspect-[4/5] md:aspect-[4/5] h-full">
              <img src={SHOWCASE_STACK} alt="Sydänkulhojen pino" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="product-image-wrap aspect-[4/3]">
              <img src={SHOWCASE_BUNNY} alt="Kanikulho, jossa on sydän" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="product-image-wrap aspect-[4/3]">
              <img src={SHOWCASE_TRIO} alt="Kolme keramiikkakania kirjan päällä" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24">
        <div className="flex items-end justify-between mb-16">
          <div>
            <div className="label-caps text-muted-foreground mb-3 flex items-center gap-2">
              <Heart strokeWidth={2} className="w-4 h-4 text-[hsl(var(--primary))] fill-[hsl(var(--primary))]" />
              01 — {t("shop.title")}
            </div>
            <h2 className="font-hand text-5xl md:text-6xl leading-tight max-w-xl">
              {t("shop.subtitle")}
            </h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex label-caps link-underline" data-testid="home-view-all">
            {t("hero.cta_shop")} →
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} className={i === 0 ? "lg:col-span-2" : ""} />
          ))}
        </div>
      </section>

      {/* Studio strip */}
      <section
        className="border-y border-border/60"
        style={{ background: "hsl(var(--rose-soft))" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <div className="product-image-wrap aspect-[5/4]">
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_ceramic-shop-18/artifacts/16idw7wn_Pikkurumiluksia-Katja.jpeg"
              alt="Katja Pennala — Pikkurumiluksia"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <div className="label-caps text-muted-foreground mb-3">02 — Studio</div>
            <h2 className="font-hand text-5xl md:text-6xl leading-tight mb-6">
              {c("story_title")}
            </h2>
            <p className="text-base md:text-lg text-foreground/70 leading-relaxed max-w-lg">
              {c("story_body")}
            </p>
          </div>
        </div>
      </section>

      <InstagramGallery />
    </main>
  );
}
